/**
 * MorososDialog (rc99) — drill-down de la tarjeta "Cartera vencida hoy":
 * clientes morosos ordenados por monto vencido desc, con número de
 * mensualidades y días de la más vieja. Cada fila abre la ficha del cliente.
 */
import { Link } from "react-router-dom";
import { Loader2, AlertCircle } from "lucide-react";

import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { formatMXN, cn } from "@/lib/utils";

import { useMorosos } from "./api";

type Props = {
  open: boolean;
  empresaId: number | null;
  onClose: () => void;
};

export function MorososDialog({ open, empresaId, onClose }: Props) {
  const { data, isLoading } = useMorosos(empresaId, open);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }} size="2xl">
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle size={16} className="text-danger" /> Clientes morosos hoy
          </DialogTitle>
          <DialogDescription>
            Mensualidades vencidas con saldo. Ordenado por monto — los de
            arriba son a quienes hay que hablarles primero.
          </DialogDescription>
        </DialogHeader>

        {isLoading || !data ? (
          <div className="py-8 text-center text-xs text-muted-foreground">
            <Loader2 size={16} className="mx-auto mb-2 animate-spin" />
            cargando…
          </div>
        ) : data.items.length === 0 ? (
          <div className="rounded-md border bg-muted/20 p-4 text-center text-xs text-emerald-600 dark:text-emerald-400">
            Cero morosos. Cartera limpia.
          </div>
        ) : (
          <>
            <div className="flex items-baseline justify-between font-mono text-xs">
              <span className="text-muted-foreground">
                {data.total_clientes} cliente(s)
              </span>
              <span className="font-semibold text-danger">
                {formatMXN(Number(data.total_monto))}
              </span>
            </div>
            <div className="max-h-[50vh] overflow-y-auto rounded-md border divide-y">
              {data.items.map((m) => (
                <Link
                  key={m.cliente_id}
                  to={`/clientes/${m.cliente_id}`}
                  className="flex items-center gap-3 px-3 py-2 text-xs hover:bg-muted/30"
                >
                  <span className="font-mono text-muted-foreground shrink-0">
                    {m.cliente_codigo}
                  </span>
                  <span className="flex-1 min-w-0 truncate">{m.cliente_nombre}</span>
                  <span className="font-mono text-[10px] text-muted-foreground shrink-0">
                    {m.facturas} fact.
                  </span>
                  <span
                    className={cn(
                      "font-mono text-[10px] shrink-0",
                      m.dias_vencido_max > 60
                        ? "text-danger"
                        : m.dias_vencido_max > 30
                          ? "text-warn"
                          : "text-muted-foreground",
                    )}
                  >
                    {m.dias_vencido_max}d
                  </span>
                  <span className="font-mono tabular-nums font-semibold shrink-0 text-right w-24">
                    {formatMXN(Number(m.monto))}
                  </span>
                </Link>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
